/**
 * Zone principale de l'espace de travail : toutes les pages ouvertes restent montées (état conservé) ; une ou deux
 * sont visibles (côte à côte au-delà de 1100 px, séparateur réglable). Chaque page a son propre routeur React Router
 * (composant bas niveau `Router` : adresse et historique propres), sans routeur englobant.
 */
import React from 'react';
import { Navigate, parsePath, Route, Router, Routes, type Navigator } from 'react-router-dom';
import { Icon, ModuleTag, cx, type Metier } from '../../design';
import { useT } from '../i18n';
import { FICHES } from '../../metiers/registre';
import { Accueil } from '../ecrans/Accueil';
import { Reglages } from '../ecrans/Reglages';
import { AdministrationConversations } from '../ecrans/AdministrationConversations';
import { AdministrationDroits } from '../ecrans/AdministrationDroits';
import { AdministrationModules } from '../ecrans/AdministrationModules';
import { Notifications } from '../ecrans/Notifications';
import { PageFiche, PageNoeud } from '../ecrans/PageNoeud';
import { useLargeur } from '../shell/utilitaires';
import { cheminDe } from './navigateur';
import {
  CtxPage, RATIO_MAX, RATIO_MIN, cheminSeul, routeDe, useActionsEspace, useEtatEspace, type PageEspace,
} from './espace';

/** Largeur minimale pour afficher deux pages côte à côte. */
export const LARGEUR_COTE_A_COTE = 1100;

/** Routes d'une page (identiques pour toutes les pages ouvertes). */
function RoutesPage() {
  return (
    <Routes>
      <Route index element={<Accueil />} />
      <Route path="reglages" element={<Reglages />} />
      {/* Mon profil : le compte est dans les Réglages ; Notifications = écran dédié (dock › cloche, menu Profil). */}
      <Route path="profil" element={<Navigate to="/reglages#compte" replace />} />
      <Route path="profil/notifications" element={<Notifications />} />
      <Route path="referentiels" element={<Navigate to="/referentiels/tiers" replace />} />
      <Route path="administration" element={<Navigate to="/administration/modules" replace />} />
      <Route path="administration/modules" element={<AdministrationModules />} />
      <Route path="administration/droits" element={<AdministrationDroits />} />
      <Route path="administration/conversations" element={<AdministrationConversations />} />
      {FICHES.map((f) => <Route key={f.chemin} path={f.chemin} element={<PageFiche fiche={f} />} />)}
      <Route path="*" element={<PageNoeud />} />
    </Routes>
  );
}

interface PropsCadre {
  page: PageEspace;
  /** Colonne (0 = gauche, 1 = droite) ou null si la page est en arrière-plan. */
  colonne: 0 | 1 | null;
  active: boolean;
  cote: boolean;
  entree: boolean;
  titre: string;
  metier: Metier;
  module: string;
}

/** Une page ouverte : routeur propre, conteneur défilant, barre de volet en côte à côte. */
const CadrePage = React.memo(function CadrePage({ page, colonne, active, cote, entree, titre, metier, module }: PropsCadre) {
  const { t } = useT();
  const actions = useActionsEspace();
  const route = routeDe(page);
  const ref = React.useRef<HTMLElement>(null);
  const visible = colonne !== null;

  const navigateur = React.useMemo<Navigator>(() => ({
    createHref: (to) => cheminDe(to),
    push: (to) => actions.naviguer(page.id, cheminDe(to)),
    replace: (to) => actions.naviguer(page.id, cheminDe(to), true),
    go: (n) => actions.aller(page.id, n),
  }), [page.id, actions]);
  const location = React.useMemo(() => ({ ...parsePath(route), state: null, key: `${page.id}:${page.index}` }), [route, page.id, page.index]);
  const ctx = React.useMemo(() => ({ id: page.id }), [page.id]);

  // Défilement : mémorisé tant que la page est visible, restauré quand elle le redevient (une page en
  // arrière-plan perd sa position de défilement) ; changement d'écran dans la page : retour en haut.
  const defilement = React.useRef(0);
  React.useLayoutEffect(() => { if (visible && ref.current) ref.current.scrollTop = defilement.current; }, [visible]);
  const chemin = cheminSeul(route);
  React.useLayoutEffect(() => { defilement.current = 0; if (ref.current) ref.current.scrollTop = 0; }, [chemin]);

  const activer = cote && visible && !active ? () => actions.activer(colonne!) : undefined;
  return (
    <section
      ref={ref}
      className={cx('agx-volet', !visible && 'agx-volet-cache', cote && visible && 'agx-volet-cadre', cote && active && 'agx-volet-actif', entree && 'agx-entree')}
      data-page-id={page.id}
      style={visible ? { gridColumn: colonne === 1 ? 3 : 1 } : undefined}
      aria-label={titre}
      aria-hidden={visible ? undefined : true}
      // Cible du lien d'évitement « Aller au contenu » (focus par script, pas d'arrêt de tabulation).
      tabIndex={visible ? -1 : undefined}
      {...(visible ? {} : { inert: '' })}
      onPointerDownCapture={activer}
      onFocusCapture={activer}
      onScroll={visible ? (e) => { defilement.current = e.currentTarget.scrollTop; } : undefined}
    >
      {cote && visible ? (
        <div className="agx-barre-volet">
          <ModuleTag metier={metier} label={module} />
          <b className="agx-barre-titre" title={titre}>{titre}</b>
          <button type="button" className="ag-page-x" aria-label={`${t('dock.afficherSeule')} : ${titre}`} title={t('dock.afficherSeule')} onClick={() => actions.seule(colonne!)}>
            <Icon name="sidebar" size={14} />
          </button>
          <button type="button" className="ag-page-x" aria-label={`${t('dock.fermerPage')} : ${titre}`} title={t('dock.fermerPage')} onClick={() => actions.fermer(page.id)}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ) : null}
      <Router location={location} navigator={navigateur} future={{ v7_relativeSplatPath: true }}>
        <CtxPage.Provider value={ctx}>
          <RoutesPage />
        </CtxPage.Provider>
      </Router>
    </section>
  );
});

function Separateur({ ratio, conteneur }: { ratio: number; conteneur: React.RefObject<HTMLDivElement | null> }) {
  const { t } = useT();
  const { definirRatio } = useActionsEspace();
  const glisser = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const cible = e.currentTarget;
    cible.setPointerCapture(e.pointerId);
    cible.classList.add('agx-separateur-actif');
    const bouger = (ev: PointerEvent) => {
      const r = conteneur.current?.getBoundingClientRect();
      if (r && r.width) definirRatio(((ev.clientX - r.left) / r.width) * 100);
    };
    const fin = () => {
      cible.classList.remove('agx-separateur-actif');
      cible.removeEventListener('pointermove', bouger);
      cible.removeEventListener('pointerup', fin);
      cible.removeEventListener('pointercancel', fin);
    };
    cible.addEventListener('pointermove', bouger);
    cible.addEventListener('pointerup', fin);
    cible.addEventListener('pointercancel', fin);
  };
  return (
    <div
      className="agx-separateur" role="separator" aria-orientation="vertical" tabIndex={0}
      aria-label={t('dock.separateur')} aria-valuemin={RATIO_MIN} aria-valuemax={RATIO_MAX} aria-valuenow={Math.round(ratio)}
      style={{ gridColumn: 2, gridRow: 1 }}
      onPointerDown={glisser}
      onDoubleClick={() => definirRatio(50)}
      onKeyDown={(e) => {
        const pas = e.shiftKey ? 10 : 5;
        if (e.key === 'ArrowLeft') { e.preventDefault(); definirRatio(ratio - pas); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); definirRatio(ratio + pas); }
        else if (e.key === 'Home') { e.preventDefault(); definirRatio(RATIO_MIN); }
        else if (e.key === 'End') { e.preventDefault(); definirRatio(RATIO_MAX); }
        else if (e.key === 'Enter') { e.preventDefault(); definirRatio(50); }
      }}
    >
      <span aria-hidden="true" />
    </div>
  );
}

export function Volets() {
  const { pages, volets, actif, ratio, entree, titreDe, infosDe } = useEtatEspace();
  const conteneur = React.useRef<HTMLDivElement>(null);
  const cote = useLargeur() >= LARGEUR_COTE_A_COTE && volets.length === 2;
  // Écran étroit : seul le volet actif est affiché (la disposition côte à côte est conservée pour plus tard).
  const visibles = cote ? volets : [volets[actif]];
  return (
    <div
      ref={conteneur}
      className={cx('agx-volets', cote && 'agx-volets-cote')}
      style={cote ? { gridTemplateColumns: `minmax(0, ${ratio}fr) 12px minmax(0, ${100 - ratio}fr)` } : undefined}
    >
      {pages.map((p) => {
        const i = visibles.indexOf(p.id);
        const { metier, module } = infosDe(p);
        return (
          <CadrePage
            key={p.id} page={p} colonne={i < 0 ? null : (i as 0 | 1)} active={p.id === volets[actif]} cote={cote}
            entree={entree === p.id} titre={titreDe(p)} metier={metier} module={module}
          />
        );
      })}
      {cote ? <Separateur ratio={ratio} conteneur={conteneur} /> : null}
    </div>
  );
}
